import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller, FieldValues, ControllerRenderProps } from 'react-hook-form';
import {
  Container,
  Paper,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Box,
  IconButton,
  InputAdornment
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import ClearIcon from '@mui/icons-material/Clear';

// Services
import {
  getInventoryItemById,
  updateInventoryItem,
  getAllWarehouses,
  createWarehouse
} from '../services/inventoryService';
import { getMainCategories, getSubcategories } from '../services/categoryService';
import { uploadImage } from '../services/cloudinaryService';
import { uploadProductImage, updateProductWithImage, prepareImagesForFirestore } from '../services/productImageService';
import MultipleImageUpload from '../components/common/MultipleImageUpload';
import { convertToBase64 } from '../utils/imageUtils';

// Types
import { WarehouseQuantity, Warehouse, InventoryItem, ProductImage } from '../utils/types';

// Form data type
type FormData = {
  id: string;
  name: string;
  sku: string;
  barcode: string;
  category: string;
  categoryId: string;
  subcategory?: string;
  subcategoryId?: string;
  purchasePrice: number;
  sellingPrice: number;
  // أسعار متعددة للمنتج
  posPrice?: number; // سعر نقطة البيع (POS)
  storePrice?: number; // سعر المتجر الإلكتروني
  wholesalePrice?: number; // سعر الجملة
  quantity: number;
  minQuantity: number;
  description: string;
  supplier: string;
  warehouseId: string;
  warehouseName: string;
  warehouseQuantities: WarehouseQuantity[];
  image: string;
  imageUrl?: string;       // رابط الصورة الرئيسي
  imgbbUrl?: string;       // رابط ImgBB
  imagekitUrl?: string;    // رابط ImageKit.io
  additionalImages?: ProductImage[]; // مصفوفة من الصور الإضافية للمنتج
  showInStore: boolean;
  featured: boolean; // منتج مميز
  bestSeller: boolean; // منتج الأكثر مبيعًا
};

/**
 * صفحة تعديل عنصر المخزون
 */
const NewEditInventoryPage: React.FC = () => {
  // استخراج معرف المنتج من الرابط
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // State variables
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseQuantities, setWarehouseQuantities] = useState<WarehouseQuantity[]>([]);
  const [categories, setCategories] = useState<{id: string, name: string}[]>([]);
  const [subcategories, setSubcategories] = useState<{id: string, name: string}[]>([]);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [productImages, setProductImages] = useState<ProductImage[]>([]);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // React Hook Form
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      id: '',
      name: '',
      sku: '',
      barcode: '',
      category: '',
      categoryId: '',
      subcategory: '',
      subcategoryId: '',
      purchasePrice: 0,
      sellingPrice: 0,
      quantity: 0,
      minQuantity: 0,
      description: '',
      supplier: '',
      warehouseId: '',
      warehouseName: '',
      warehouseQuantities: [],
      image: '',
      showInStore: true,
      featured: false,
      bestSeller: false
    }
  });

  // Watch for category changes
  const selectedCategory = watch('categoryId');

  // تحميل بيانات المنتج
  useEffect(() => {
    const fetchItemData = async () => {
      if (!id) {
        setError('معرف العنصر غير متوفر');
        setIsLoading(false);
        return;
      }

      try {
        console.log('Loading item data for ID:', id);
        const fetchedItem = await getInventoryItemById(id);

        if (fetchedItem) {
          console.log('Item loaded successfully:', fetchedItem.name);

          // تعيين قيم النموذج من البيانات المستردة
          setValue('id', fetchedItem.id);
          setValue('name', fetchedItem.name);
          setValue('sku', fetchedItem.sku || '');
          setValue('barcode', fetchedItem.barcode || '');
          setValue('category', fetchedItem.category || '');
          setValue('categoryId', fetchedItem.categoryId || '');
          setValue('subcategory', fetchedItem.subcategory || '');
          setValue('subcategoryId', fetchedItem.subcategoryId || '');
          setValue('purchasePrice', fetchedItem.purchasePrice || 0);
          setValue('sellingPrice', fetchedItem.sellingPrice || 0);
          // تعيين الأسعار المتعددة
          if (fetchedItem.posPrice !== undefined && fetchedItem.posPrice !== null) {
            setValue('posPrice', fetchedItem.posPrice);
          }
          if (fetchedItem.storePrice !== undefined && fetchedItem.storePrice !== null) {
            setValue('storePrice', fetchedItem.storePrice);
          }
          if (fetchedItem.wholesalePrice !== undefined && fetchedItem.wholesalePrice !== null) {
            setValue('wholesalePrice', fetchedItem.wholesalePrice);
          }
          setValue('quantity', fetchedItem.quantity || 0);
          setValue('minQuantity', fetchedItem.minQuantity || 0);
          setValue('description', fetchedItem.description || '');
          setValue('supplier', fetchedItem.supplier || '');
          setValue('warehouseId', fetchedItem.warehouseId || '');
          setValue('warehouseName', fetchedItem.warehouseName || '');
          setValue('image', fetchedItem.image || fetchedItem.imageUrl || '');
          setValue('imageUrl', fetchedItem.imageUrl || '');
          setValue('imgbbUrl', fetchedItem.imgbbUrl || '');
          setValue('imagekitUrl', fetchedItem.imagekitUrl || '');
          setValue('showInStore', fetchedItem.showInStore !== undefined ? fetchedItem.showInStore : true);
          setValue('featured', fetchedItem.featured !== undefined ? fetchedItem.featured : false);
          setValue('bestSeller', fetchedItem.bestSeller !== undefined ? fetchedItem.bestSeller : false);

          // تعيين الصور الإضافية إذا كانت متوفرة
          if (fetchedItem.additionalImages && Array.isArray(fetchedItem.additionalImages)) {
            console.log('تم العثور على صور إضافية:', fetchedItem.additionalImages);
            setProductImages(fetchedItem.additionalImages);
          } else {
            console.log('لا توجد صور إضافية للمنتج أو الصور بتنسيق غير صحيح');
            setProductImages([]);
          }

          // تعيين كميات المخازن
          if (fetchedItem.warehouseQuantities && fetchedItem.warehouseQuantities.length > 0) {
            console.log('Setting warehouse quantities from item:', fetchedItem.warehouseQuantities);
            setWarehouseQuantities(fetchedItem.warehouseQuantities);
            setValue('warehouseQuantities', fetchedItem.warehouseQuantities);
          } else if (fetchedItem.warehouseId) {
            // إنشاء كميات المخازن من البيانات القديمة
            const initialWarehouseQuantities = [{
              warehouseId: fetchedItem.warehouseId,
              warehouseName: fetchedItem.warehouseName || 'المستودع الافتراضي',
              quantity: fetchedItem.quantity || 0
            }];
            console.log('Created initial warehouse quantities:', initialWarehouseQuantities);
            setWarehouseQuantities(initialWarehouseQuantities);
            setValue('warehouseQuantities', initialWarehouseQuantities);
          }

          // تعيين معاينة الصورة إذا كانت متوفرة
          if (fetchedItem.image || fetchedItem.imageUrl) {
            setPreviewUrl(fetchedItem.image || fetchedItem.imageUrl || null);
          }

          setIsLoading(false);
        } else {
          setError('لم يتم العثور على العنصر');
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error fetching item:', err);
        setError('حدث خطأ أثناء تحميل بيانات العنصر');
        setIsLoading(false);
      }
    };

    fetchItemData();
  }, [id, setValue]);

  // تحميل الفئات عند تحميل الصفحة
  useEffect(() => {
    const loadCategories = async () => {
      try {
        console.log('Loading main categories...');
        // استخدام forceRefresh = true لضمان تحميل البيانات من قاعدة البيانات
        const categoriesData = await getMainCategories(true);
        console.log('Loaded categories:', categoriesData.length);

        if (categoriesData.length === 0) {
          console.warn('No categories found, trying again without force refresh');
          const fallbackData = await getMainCategories(false);
          console.log('Fallback categories loaded:', fallbackData.length);

          setCategories(fallbackData.map(cat => ({
            id: cat.id,
            name: cat.name
          })));
        } else {
          setCategories(categoriesData.map(cat => ({
            id: cat.id,
            name: cat.name
          })));
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        setError('فشل في تحميل الفئات');
      }
    };

    loadCategories();
  }, []);

  // تحميل الفئات الفرعية عند تغيير الفئة الرئيسية
  useEffect(() => {
    const loadSubcategories = async () => {
      // إعادة تعيين الفئات الفرعية عند البدء أو إذا لم يتم تحديد فئة رئيسية
      setSubcategories([]);

      if (selectedCategory) {
        try {
          console.log('Loading subcategories for category ID:', selectedCategory);
          const subcatsData = await getSubcategories(selectedCategory, true);

          if (subcatsData && Array.isArray(subcatsData) && subcatsData.length > 0) {
            // فلترة البيانات الصالحة فقط
            const validSubcats = subcatsData.filter(subcat => subcat && subcat.id && subcat.name);
            console.log('Received valid subcategories:', validSubcats.length);

            // تعيين الفئات الفرعية الصالحة
            setSubcategories(validSubcats.map(subcat => ({
              id: subcat.id,
              name: subcat.name
            })));
          } else {
            console.log('No subcategories found for category ID:', selectedCategory);
            setSubcategories([]); // التأكد من أن الحالة فارغة
          }
        } catch (error) {
          console.error('Error loading subcategories:', error);
          setSubcategories([]); // التأكد من أن الحالة فارغة عند الخطأ
        }
      }
    };

    loadSubcategories();
  }, [selectedCategory]);

  // تحميل المخازن عند تحميل الصفحة
  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        console.log('Loading warehouses...');
        const warehouseData = await getAllWarehouses();
        console.log('Warehouses loaded:', warehouseData);

        // التحقق من وجود مخازن
        if (!warehouseData || warehouseData.length === 0) {
          console.warn('No warehouses available, creating a default warehouse');

          // إنشاء مخزن افتراضي إذا لم يكن هناك مخازن
          try {
            const defaultWarehouseId = await createWarehouse('المخزن الرئيسي', 'الموقع الافتراضي');
            console.log('Created default warehouse with ID:', defaultWarehouseId);

            // إعادة تحميل المخازن بعد إنشاء المخزن الافتراضي
            const updatedWarehouseData = await getAllWarehouses();
            console.log('Warehouses reloaded after creating default:', updatedWarehouseData);
            setWarehouses(updatedWarehouseData);

            if (updatedWarehouseData.length > 0) {
              console.log('Setting default warehouse:', updatedWarehouseData[0].name);
              setValue('warehouseId', updatedWarehouseData[0].id);
              setValue('warehouseName', updatedWarehouseData[0].name);
            }
          } catch (createError) {
            console.error('Error creating default warehouse:', createError);
            setError('فشل في إنشاء المخزن الافتراضي');
          }
        } else {
          // تعيين المخازن الموجودة
          setWarehouses(warehouseData);
        }
      } catch (error) {
        console.error('Error loading warehouses:', error);
        setError('فشل في تحميل المخازن');
      }
    };

    loadWarehouses();
  }, [setValue]);

  // معالجة تغيير كميات المخازن
  const handleWarehouseQuantityChange = (quantities: WarehouseQuantity[]) => {
    console.log('Warehouse quantities changed:', quantities);

    // التأكد من الحفاظ على قيمة الموقع لكل مخزن
    const updatedQuantities = quantities.map(q => {
      // إذا لم يكن هناك موقع محدد، نضيف سلسلة فارغة
      if (!q.location) {
        return { ...q, location: '' };
      }
      return q;
    });

    setWarehouseQuantities(updatedQuantities);
    setValue('warehouseQuantities', updatedQuantities);

    // حساب الكمية الإجمالية
    const totalQuantity = updatedQuantities.reduce((sum, item) => sum + (item.quantity || 0), 0);
    console.log('Total quantity calculated:', totalQuantity);
    setValue('quantity', totalQuantity);
  };

  // معالجة اختيار الصورة
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      // إعادة تعيين رسالة الخطأ قبل معالجة الصورة الجديدة
      setError('');

      const file = event.target.files[0];
      console.log('تم اختيار صورة:', file.name, 'حجم:', (file.size / 1024).toFixed(2) + 'KB', 'نوع:', file.type);

      // التحقق من نوع الملف (يجب أن يكون صورة)
      if (!file.type.startsWith('image/')) {
        setError('الرجاء اختيار ملف صورة صالح (jpg, png, gif, etc)');
        // إعادة تعيين قيمة حقل الإدخال
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // التحقق من حجم الملف (أقل من 5 ميجابايت)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setError('حجم الصورة كبير جداً. الحد الأقصى هو 5 ميجابايت');
        // إعادة تعيين قيمة حقل الإدخال
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setSelectedImage(file);

      // إنشاء معاينة للصورة
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result && typeof reader.result === 'string') {
          setPreviewUrl(reader.result);
          console.log('تم إنشاء معاينة الصورة بنجاح');
        } else {
          console.error('نتيجة القراءة غير صالحة');
          setError('فشل في إنشاء معاينة الصورة');
        }
      };
      reader.onerror = () => {
        console.error('فشل في قراءة الصورة');
        setError('فشل في قراءة الصورة، يرجى المحاولة مرة أخرى');
        setSelectedImage(null);
        setPreviewUrl(null);
      };
      reader.readAsDataURL(file);
    }
  };

  // إزالة الصورة المختارة
  const handleRemoveImage = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    setValue('image', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // تحميل الصورة باستخدام الخدمة الموحدة
  const uploadImageToCloudinary = async (file: File): Promise<string> => {
    setIsUploading(true);
    try {
      console.log('بدء عملية تحميل الصورة للمنتج...');

      // استخدام الخدمة الموحدة
      const { uploadUnifiedImage } = await import('../services/unifiedImageService');
      const imageResult = await uploadUnifiedImage({
        file: file,
        context: 'inventory',
        contextId: id,
        folder: 'inventory'
      });
      console.log('تم تحميل الصورة بنجاح:', imageResult.url.substring(0, 50) + '...');
      return imageResult.url;
    } catch (error) {
      console.error('خطأ في تحميل الصورة:', error);
      setError('فشل تحميل الصورة. الرجاء المحاولة مرة أخرى.');
      throw new Error('فشل تحميل الصورة: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
    } finally {
      setIsUploading(false);
    }
  };

  // معالجة تقديم النموذج
  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setError('');

    try {
      // التحقق من وجود معرف المنتج
      if (!data.id) {
        throw new Error('معرف العنصر غير موجود');
      }

      // التحقق من وجود اسم المنتج
      if (!data.name || data.name.trim() === '') {
        throw new Error('يرجى إدخال اسم المنتج');
      }

      let imageUrl = data.image;

      // إذا تم اختيار صورة جديدة، قم بتحميلها
      if (selectedImage) {
        try {
          console.log('تحميل الصورة الجديدة للمنتج...');

          // استخدام الخدمة الموحدة
          const { uploadUnifiedImage } = await import('../services/unifiedImageService');
          const imageResult = await uploadUnifiedImage({
            file: selectedImage,
            context: 'inventory',
            contextId: id,
            folder: 'inventory'
          });
          console.log('تم تحميل الصورة بنجاح، النتيجة:', imageResult);

          // تحديث بيانات المنتج بجميع روابط الصورة
          data.image = imageResult.url;
          data.imageUrl = imageResult.url;
          data.imgbbUrl = imageResult.imgbbUrl;
          data.imagekitUrl = imageResult.imagekitUrl;

          imageUrl = imageResult.url;

          console.log('تم تحديث بيانات المنتج بروابط الصورة الجديدة');
        } catch (uploadError) {
          console.error('خطأ في تحميل الصورة:', uploadError);
          // المتابعة بدون صورة بدلاً من فشل العملية بالكامل
          setError(`فشل في تحميل الصورة، ولكن سيتم متابعة تحديث المنتج: ${uploadError instanceof Error ? uploadError.message : 'خطأ غير معروف'}`);
          // الاستمرار في العملية بدون تحميل صورة جديدة
        }
      }

      // التحقق من وجود كميات المخازن
      if (!data.warehouseQuantities || data.warehouseQuantities.length === 0) {
        console.log('No warehouse quantities specified in form data');

        // التحقق من وجود مخازن
        if (warehouses.length === 0) {
          console.log('No warehouses available, creating a default warehouse');

          try {
            // إنشاء مخزن افتراضي إذا لم يكن هناك مخازن
            const defaultWarehouseId = await createWarehouse('المخزن الرئيسي', 'الموقع الافتراضي');
            console.log('Created default warehouse with ID:', defaultWarehouseId);

            // إعادة تحميل المخازن
            const updatedWarehouses = await getAllWarehouses();
            console.log('Warehouses reloaded:', updatedWarehouses);

            if (updatedWarehouses.length > 0) {
              // تحديث المخازن المحلية
              setWarehouses(updatedWarehouses);

              // إضافة المخزن الافتراضي إلى الكميات
              data.warehouseId = updatedWarehouses[0].id;
              data.warehouseName = updatedWarehouses[0].name;
              data.warehouseQuantities = [{
                warehouseId: updatedWarehouses[0].id,
                warehouseName: updatedWarehouses[0].name,
                quantity: data.quantity || 0,
                location: ''
              }];
              console.log('Created default warehouse quantity:', data.warehouseQuantities);
            } else {
              throw new Error('فشل في إنشاء المخزن الافتراضي');
            }
          } catch (createError) {
            console.error('Error creating default warehouse:', createError);
            throw new Error('فشل في إنشاء المخزن الافتراضي');
          }
        } else {
          // استخدام المخزن الافتراضي الموجود
          console.log('Using default warehouse:', warehouses[0].name);
          data.warehouseQuantities = [{
            warehouseId: data.warehouseId || warehouses[0].id,
            warehouseName: data.warehouseName || warehouses[0].name,
            quantity: data.quantity || 0,
            location: ''
          }];
          console.log('Created default warehouse quantity:', data.warehouseQuantities);
        }
      }

      // حساب الكمية الإجمالية من كميات المخازن
      const totalQuantity = data.warehouseQuantities.reduce((sum, wq) => sum + (wq.quantity || 0), 0);
      console.log('Total quantity calculated from warehouse quantities:', totalQuantity);
      data.quantity = totalQuantity;

      // تحضير بيانات المنتج للإرسال
      // تحضير الصور الإضافية أولاً
      const preparedImages = prepareImagesForFirestore(productImages);
      console.log('الصور الإضافية بعد التحويل:', preparedImages);

      const inventoryItem: InventoryItem = {
        id: data.id,
        name: data.name,
        sku: data.sku || `SKU-${Date.now()}`,
        barcode: data.barcode || '',
        category: data.category || '',
        categoryId: data.categoryId || '',
        subcategory: data.subcategory || '',
        subcategoryId: data.subcategoryId || '',
        purchasePrice: data.purchasePrice || 0,
        sellingPrice: data.sellingPrice || 0,
        // أسعار متعددة للمنتج
        posPrice: data.posPrice !== undefined && data.posPrice !== null ? data.posPrice : undefined,
        storePrice: data.storePrice !== undefined && data.storePrice !== null ? data.storePrice : undefined,
        wholesalePrice: data.wholesalePrice !== undefined && data.wholesalePrice !== null ? data.wholesalePrice : undefined,
        quantity: data.quantity || 0,
        minQuantity: data.minQuantity || 0,
        description: data.description || '',
        supplier: data.supplier || '',
        warehouseId: data.warehouseId || warehouses[0]?.id || '',
        warehouseName: data.warehouseName || warehouses[0]?.name || '',
        warehouseQuantities: data.warehouseQuantities || [],
        image: data.image || '', // استخدام القيمة من النموذج
        imageUrl: data.imageUrl || '', // استخدام القيمة من النموذج
        imgbbUrl: data.imgbbUrl || '', // إضافة رابط ImgBB
        imagekitUrl: data.imagekitUrl || '', // إضافة رابط ImageKit.io
        additionalImages: preparedImages, // إضافة الصور الإضافية بعد تحويلها إلى تنسيق صالح
        showInStore: data.showInStore === undefined ? false : data.showInStore,
        featured: data.featured === undefined ? false : data.featured, // منتج مميز
        bestSeller: data.bestSeller === undefined ? false : data.bestSeller, // منتج الأكثر مبيعًا
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // تسجيل معلومات الصور الإضافية للتأكد من أنها تعمل بشكل صحيح
      console.log('الصور الإضافية قبل التحويل:', productImages);
      // الصور الإضافية تم تحضيرها بالفعل أعلاه
      console.log('إرسال بيانات المنتج مع الصور الإضافية:', inventoryItem.additionalImages);

      console.log('إرسال بيانات المنتج للتحديث:', inventoryItem);
      console.log('الأسعار المتعددة:', {
        posPrice: inventoryItem.posPrice,
        storePrice: inventoryItem.storePrice,
        wholesalePrice: inventoryItem.wholesalePrice
      });
      await updateInventoryItem(data.id, inventoryItem);
      console.log('تم تحديث المنتج بنجاح');
      setSuccess('تم تحديث المنتج بنجاح');

      // العودة إلى صفحة المخزون بعد التحديث
      setTimeout(() => {
        navigate('/admin/inventory');
      }, 2000);
    } catch (err) {
      console.error('Error updating inventory item:', err);
      setError(err instanceof Error ? err.message : 'حدث خطأ أثناء تحديث العنصر');
    } finally {
      setIsLoading(false);
    }
  };

  // عرض شاشة التحميل
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          تعديل عنصر المخزون
        </Typography>

        {/* رسائل الخطأ والنجاح */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {/* معلومات المنتج الأساسية */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }}>
                <Typography variant="h6">معلومات المنتج الأساسية</Typography>
              </Divider>
            </Grid>

            {/* اسم المنتج */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" error={!!errors.name}>
                <TextField
                  label="اسم المنتج *"
                  {...register('name', { required: 'اسم المنتج مطلوب' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </FormControl>
            </Grid>

            {/* رمز المنتج (SKU) */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="رمز المنتج (SKU)"
                  {...register('sku')}
                  placeholder="سيتم إنشاؤه تلقائيًا إذا ترك فارغًا"
                />
              </FormControl>
            </Grid>

            {/* الباركود */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="الباركود"
                  {...register('barcode')}
                />
              </FormControl>
            </Grid>

            {/* الفئة الرئيسية */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="category-label">الفئة</InputLabel>
                <Controller
                  name="categoryId"
                  control={control}
                  render={({ field }: { field: ControllerRenderProps<FormData, 'categoryId'> }) => (
                    <Select
                      labelId="category-label"
                      label="الفئة"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        // البحث عن اسم الفئة المقابل للمعرف
                        const selectedCategory = categories.find(cat => cat.id === e.target.value);
                        if (selectedCategory) {
                          setValue('category', selectedCategory.name);
                        }
                      }}
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            {/* الفئة الفرعية (تظهر فقط إذا تم اختيار فئة رئيسية) */}
            {subcategories.length > 0 && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="subcategory-label">الفئة الفرعية</InputLabel>
                  <Controller
                    name="subcategoryId"
                    control={control}
                    render={({ field }: { field: ControllerRenderProps<FormData, 'subcategoryId'> }) => (
                      <Select
                        labelId="subcategory-label"
                        label="الفئة الفرعية"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          // البحث عن اسم الفئة الفرعية المقابل للمعرف
                          const selectedSubcategory = subcategories.find(subcat => subcat.id === e.target.value);
                          if (selectedSubcategory) {
                            setValue('subcategory', selectedSubcategory.name);
                          }
                        }}
                      >
                        {subcategories.map((subcategory) => (
                          <MenuItem key={subcategory.id} value={subcategory.id}>
                            {subcategory.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
            )}

            {/* الوصف */}
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="الوصف"
                  multiline
                  rows={4}
                  {...register('description')}
                />
              </FormControl>
            </Grid>

            {/* المورد */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="المورد"
                  {...register('supplier')}
                />
              </FormControl>
            </Grid>

            {/* عرض في المتجر */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormControlLabel
                  control={
                    <Controller
                      name="showInStore"
                      control={control}
                      render={({ field }: { field: ControllerRenderProps<FormData, 'showInStore'> }) => (
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="عرض في المتجر"
                />
              </FormControl>
            </Grid>

            {/* منتج مميز */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormControlLabel
                  control={
                    <Controller
                      name="featured"
                      control={control}
                      render={({ field }: { field: ControllerRenderProps<FormData, 'featured'> }) => (
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="منتج مميز"
                />
              </FormControl>
            </Grid>

            {/* منتج الأكثر مبيعًا */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormControlLabel
                  control={
                    <Controller
                      name="bestSeller"
                      control={control}
                      render={({ field }: { field: ControllerRenderProps<FormData, 'bestSeller'> }) => (
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="الأكثر مبيعًا"
                />
              </FormControl>
            </Grid>

            {/* معلومات السعر */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2, mt: 2 }}>
                <Typography variant="h6">معلومات السعر</Typography>
              </Divider>
            </Grid>

            {/* سعر الشراء */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" error={!!errors.purchasePrice}>
                <TextField
                  label="سعر الشراء"
                  type="number"
                  {...register('purchasePrice', {
                    valueAsNumber: true,
                    validate: (value: number) => value >= 0 || 'يجب أن يكون سعر الشراء عدد موجب'
                  })}
                  error={!!errors.purchasePrice}
                  helperText={errors.purchasePrice?.message}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>EGP</Typography>,
                  }}
                />
              </FormControl>
            </Grid>

            {/* سعر البيع */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" error={!!errors.sellingPrice}>
                <TextField
                  label="سعر البيع"
                  type="number"
                  {...register('sellingPrice', {
                    valueAsNumber: true,
                    validate: (value: number) => value >= 0 || 'يجب أن يكون سعر البيع عدد موجب'
                  })}
                  error={!!errors.sellingPrice}
                  helperText={errors.sellingPrice?.message}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>EGP</Typography>,
                  }}
                />
              </FormControl>
            </Grid>

            {/* أسعار متعددة */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2, mt: 2 }}>
                <Typography variant="h6">أسعار متعددة (اختياري)</Typography>
              </Divider>
            </Grid>

            {/* سعر نقطة البيع (POS) */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="سعر نقطة البيع (POS)"
                  type="number"
                  {...register('posPrice', {
                    valueAsNumber: true,
                    validate: (value: number | undefined) => !value || value >= 0 || 'يجب أن يكون السعر عدد موجب'
                  })}
                  error={!!errors.posPrice}
                  helperText={errors.posPrice?.message || 'سعر البيع في نقطة البيع'}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>EGP</Typography>,
                  }}
                />
              </FormControl>
            </Grid>

            {/* سعر المتجر الإلكتروني */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="سعر المتجر الإلكتروني"
                  type="number"
                  {...register('storePrice', {
                    valueAsNumber: true,
                    validate: (value: number | undefined) => !value || value >= 0 || 'يجب أن يكون السعر عدد موجب'
                  })}
                  error={!!errors.storePrice}
                  helperText={errors.storePrice?.message || 'سعر البيع في المتجر الإلكتروني'}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>EGP</Typography>,
                  }}
                />
              </FormControl>
            </Grid>

            {/* سعر الجملة */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="سعر الجملة"
                  type="number"
                  {...register('wholesalePrice', {
                    valueAsNumber: true,
                    validate: (value: number | undefined) => !value || value >= 0 || 'يجب أن يكون السعر عدد موجب'
                  })}
                  error={!!errors.wholesalePrice}
                  helperText={errors.wholesalePrice?.message || 'سعر بيع الجملة'}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>EGP</Typography>,
                  }}
                />
              </FormControl>
            </Grid>

            {/* معلومات المخزون */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2, mt: 2 }}>
                <Typography variant="h6">معلومات المخزون</Typography>
              </Divider>
            </Grid>

            {/* الكمية الإجمالية */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: '#f8f8f8' }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#1976d2' }}>
                  الكمية الإجمالية
                </Typography>
                <FormControl fullWidth variant="outlined">
                  <TextField
                    label="الكمية الإجمالية في جميع المخازن"
                    type="number"
                    value={warehouseQuantities.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                    disabled
                    InputProps={{
                      readOnly: true,
                      sx: { fontSize: '1.2rem', fontWeight: 'bold' }
                    }}
                    sx={{ bgcolor: 'white' }}
                  />
                </FormControl>
              </Paper>
            </Grid>

            <Grid container spacing={2}>
              {/* الحد الأدنى للكمية */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" error={!!errors.minQuantity}>
                  <TextField
                    label="الحد الأدنى للكمية"
                    type="number"
                    {...register('minQuantity', {
                      valueAsNumber: true,
                      validate: (value: number) => value >= 0 || 'يجب أن يكون الحد الأدنى عدد موجب'
                    })}
                    error={!!errors.minQuantity}
                    helperText={errors.minQuantity?.message}
                  />
                </FormControl>
              </Grid>
            </Grid>

            {/* كميات المخازن */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 3, mt: 3, bgcolor: '#f8f8f8' }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#1976d2' }}>
                  كميات المخازن
                </Typography>

                {warehouses.length === 0 ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    لا توجد مخازن متاحة. يرجى إضافة مخزن أولاً.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {warehouses.map((warehouse) => (
                      <Grid item xs={12} key={warehouse.id}>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                            {warehouse.name}
                          </Typography>
                          <Grid container spacing={2}>
                            {/* حقل الكمية */}
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth variant="outlined">
                                <TextField
                                  label={`الكمية`}
                                  type="number"
                                  value={warehouseQuantities.find(q => q.warehouseId === warehouse.id)?.quantity || 0}
                                  onChange={(e) => {
                                    const newQuantities = [...warehouseQuantities];
                                    const index = newQuantities.findIndex(q => q.warehouseId === warehouse.id);
                                    if (index !== -1) {
                                      newQuantities[index].quantity = Number(e.target.value);
                                    } else {
                                      newQuantities.push({
                                        warehouseId: warehouse.id,
                                        warehouseName: warehouse.name,
                                        quantity: Number(e.target.value),
                                        location: ''
                                      });
                                    }
                                    handleWarehouseQuantityChange(newQuantities);
                                  }}
                                  inputProps={{ min: 0 }}
                                />
                              </FormControl>
                            </Grid>

                            {/* حقل الموقع في المخزن */}
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth variant="outlined">
                                <TextField
                                  label="الموقع في المخزن (الرف/الدرج/الكرتونة)"
                                  value={warehouseQuantities.find(q => q.warehouseId === warehouse.id)?.location || ''}
                                  onChange={(e) => {
                                    const newQuantities = [...warehouseQuantities];
                                    const index = newQuantities.findIndex(q => q.warehouseId === warehouse.id);
                                    if (index !== -1) {
                                      newQuantities[index].location = e.target.value;
                                    } else {
                                      newQuantities.push({
                                        warehouseId: warehouse.id,
                                        warehouseName: warehouse.name,
                                        quantity: 0,
                                        location: e.target.value
                                      });
                                    }
                                    handleWarehouseQuantityChange(newQuantities);
                                  }}
                                  placeholder="مثال: رف 3 / درج 2 / كرتونة A5"
                                />
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Paper>
            </Grid>

            {/* قسم الصورة */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2, mt: 2 }}>
                <Typography variant="h6">صورة المنتج</Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f8f8f8' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 2 }}>
                  {/* معاينة الصورة */}
                  <Box sx={{ width: { xs: '100%', sm: '200px' }, height: '200px', border: '1px dashed #ccc', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'white', mb: { xs: 2, sm: 0 } }}>
                    {previewUrl ? (
                      <Box component="img" src={previewUrl} alt="معاينة الصورة" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <Typography color="text.secondary">لا توجد صورة</Typography>
                    )}
                  </Box>

                  {/* أزرار التحكم بالصورة */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body1" gutterBottom>
                      يمكنك تحميل صورة للمنتج بحجم أقصى 5 ميجابايت
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        component="label"
                        startIcon={<PhotoCamera />}
                      >
                        اختيار صورة
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          ref={fileInputRef}
                          onChange={handleImageSelect}
                        />
                      </Button>

                      {previewUrl && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={handleRemoveImage}
                          startIcon={<ClearIcon />}
                        >
                          إزالة الصورة
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* صور إضافية للمنتج */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f8f8f8' }}>
                <Typography variant="h6" gutterBottom>
                  صور إضافية للمنتج
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  يمكنك إضافة صور متعددة للمنتج لعرضها من زوايا مختلفة أو بألوان متعددة
                </Typography>

                <MultipleImageUpload
                  images={productImages}
                  onImagesChange={setProductImages}
                  folder="inventory"
                  maxImages={10}
                />
              </Paper>
            </Grid>

            {/* أزرار الحفظ والإلغاء */}
            <Grid item xs={12} sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => navigate('/admin/inventory')}
                sx={{ minWidth: '120px' }}
              >
                إلغاء
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                onClick={handleSubmit(onSubmit)}
                disabled={isLoading}
                sx={{ minWidth: '120px' }}
              >
                {isLoading ? <CircularProgress size={24} /> : 'حفظ التغييرات'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default NewEditInventoryPage;
